import { useEffect, useMemo, useState } from 'react';
import { getBalances, getCandlePrice, getDeals, getLocationItems, getMergeSuggestions, postManualEvent, postMergeDecision } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { EventType, LocationItem, MergeSuggestion } from '../api/model';
import type { ManualEventIn } from '../api/types';
import { AllocationEditor, newAllocRow, validateAllocations, type AllocRow } from '../components/AllocationEditor';
import { Async, ErrorNotice, Notice } from '../components/Async';
import { Dialog } from '../components/Dialog';
import { PriceSourceLabel } from '../components/PriceSource';
import { RepayPanel, type RepayPanelState } from '../components/RepayPanel';
import { HelpButton, HelpLabel, HelpText } from '../components/Help';
import {
  addDecimal,
  compareDecimal,
  formatDecimal,
  isDecimalString,
  isNegativeDecimal,
  isZeroDecimal,
  mulDecimal,
  negateDecimal,
  subDecimal,
} from '../lib/decimal';
import { eventTypeText, locationText } from '../lib/labels';
import { closeMath, expectedTrueUp, interestError, principalPart } from '../lib/repayLinks';
import { reasonText } from '../i18n/reasons.hu';
import { Link, useSearchParams } from '../lib/router';
import { formatUtc, parseUtcInput, toApiUtc, utcInputValue } from '../lib/time';

type EntryKind = 'out' | 'in' | 'buy' | 'sell' | 'move' | 'borrow' | 'repay' | 'snapshot' | 'result';

const KINDS: { value: EntryKind; label: string }[] = [
  { value: 'out', label: 'Kiutalás' },
  { value: 'in', label: 'Visszautalás' },
  { value: 'buy', label: 'Vétel' },
  { value: 'sell', label: 'Eladás' },
  { value: 'move', label: 'Transzfer' },
  { value: 'borrow', label: 'Hitelfelvétel' },
  { value: 'repay', label: 'Visszafizetés' },
  { value: 'snapshot', label: 'Egyenleg-rögzítés' },
  { value: 'result', label: 'Eredmény' },
];

const API_TYPE: Record<EntryKind, EventType> = {
  out: 'transfer', in: 'transfer', move: 'transfer', buy: 'buy', sell: 'sell', borrow: 'borrow', repay: 'repay', snapshot: 'balance_snapshot', result: 'result',
};

const isTransfer = (k: EntryKind) => k === 'out' || k === 'in' || k === 'move';
const isTrade = (k: EntryKind) => k === 'buy' || k === 'sell';

/** Default location NAMES per kind (resolved against the live location list). */
const DEFAULT_LOC: Record<EntryKind, { from: string; to?: string }> = {
  out: { from: 'Binance Margin', to: 'Nexo' },
  in: { from: 'Nexo', to: 'Binance Margin' },
  move: { from: 'Binance Margin', to: 'Binance Futures' },
  buy: { from: 'Nexo' },
  sell: { from: 'Nexo' },
  borrow: { from: 'Binance Margin' },
  repay: { from: 'Binance Margin' },
  snapshot: { from: 'Binance Margin' },
  result: { from: 'Binance Margin' },
};

interface PreviewLine {
  acct: string;
  dr: string;
  cr: string;
}

interface Preview {
  lines: PreviewLine[];
  note: string | null;
}

const PH_QTY = '[MENNYISÉG]';
const PH_SUM = '[ÖSSZEG]';
const fmt = (v: string) => formatDecimal(v, { maxDp: 8 });

function line(acct: string, amount: string | null, placeholder: string): PreviewLine {
  if (amount === null) return { acct, dr: placeholder, cr: '' };
  if (isNegativeDecimal(amount)) return { acct, dr: '', cr: fmt(negateDecimal(amount)) };
  return { acct, dr: fmt(amount), cr: '' };
}

/** Pure preview of the postings (docs/architecture.md §4.3). The server books the real ones. */
export function buildPreview(args: {
  kind: EntryKind;
  loc: string;
  counter: string;
  asset: string;
  quote: string;
  qty: string;
  price: string | null;
  feeAsset: string;
  feeQty: string;
  ledgerBalance: string | null;
  /** D48: the interest part of a repay ('' / absent = none). */
  interest?: string;
  /** D48: a Binance-venue location accrues interest itself; the repay settles the accrued amount. */
  accrues?: boolean | null;
  accrued?: string | null;
  /** The repay closes the loan fully (then the server books the true-up on an accruing location). */
  closesFully?: boolean;
}): Preview {
  const { kind, loc, counter, asset, quote, qty, price, feeAsset, feeQty, ledgerBalance } = args;
  const q = isDecimalString(qty) ? qty : null;
  const a = asset.trim() || '[COIN]';
  const lines: PreviewLine[] = [];
  let note: string | null = null;
  const credit = (acct: string, amount: string | null, ph: string): PreviewLine => {
    const l = line(acct, amount === null ? null : negateDecimal(amount), ph);
    if (amount === null) return { acct, dr: '', cr: ph };
    return l;
  };
  switch (kind) {
    case 'out':
    case 'in':
    case 'move':
      lines.push(line(`${counter} · ${a}`, q, PH_QTY), credit(`${loc} · ${a}`, q, PH_QTY));
      break;
    case 'borrow':
      lines.push(line(`${loc} · ${a}`, q, PH_QTY), credit(`${loc} · loan · ${a}`, q, PH_QTY));
      break;
    case 'repay': {
      const i = args.interest && isDecimalString(args.interest) && !isZeroDecimal(args.interest) && q !== null && compareDecimal(args.interest, q) <= 0 ? args.interest : null;
      if (i === null || q === null) {
        lines.push(line(`${loc} · loan · ${a}`, q, PH_QTY), credit(`${loc} · ${a}`, q, PH_QTY));
        break;
      }
      lines.push(line(`${loc} · loan · ${a} (tőke)`, subDecimal(q, i), PH_QTY));
      if (!args.accrues) {
        lines.push(line(`Interest · ${a} (kamatköltség)`, i, PH_SUM));
      } else if (args.closesFully && args.accrued != null && isDecimalString(args.accrued)) {
        // The accrued interest clears the loan account; the difference to what was paid is the true-up (expected, the server computes it).
        lines.push(line(`${loc} · loan · ${a} (számolt kamat rendezése)`, args.accrued, PH_SUM));
        const t = expectedTrueUp(i, args.accrued);
        if (!isZeroDecimal(t)) lines.push(line(`Interest · ${a} (várható kiigazítás)`, t, PH_SUM));
      } else {
        lines.push(line(`${loc} · loan · ${a} (számolt kamat rendezése)`, i, PH_SUM));
      }
      lines.push(credit(`${loc} · ${a}`, q, PH_QTY));
      break;
    }
    case 'buy':
    case 'sell': {
      const qt = quote.trim() || '[QUOTE]';
      const sum = q !== null && price !== null && isDecimalString(price) ? mulDecimal(q, price) : null;
      if (kind === 'buy') lines.push(line(`${loc} · ${a}`, q, PH_QTY), credit(`${loc} · ${qt}`, sum, PH_SUM));
      else lines.push(line(`${loc} · ${qt}`, sum, PH_SUM), credit(`${loc} · ${a}`, q, PH_QTY));
      if (sum === null && q !== null) note = 'Az ár megadása kötelező: a szerver nem becsli meg a hiányzó árat.';
      break;
    }
    case 'snapshot': {
      if (q === null || ledgerBalance === null) {
        lines.push({ acct: `${loc} · ${a}`, dr: PH_SUM, cr: '' });
        note = ledgerBalance === null ? 'A napló szerinti egyenleg még nem ismert.' : null;
        break;
      }
      const diff = subDecimal(q, ledgerBalance);
      note = `Napló szerint: ${fmt(ledgerBalance)} ${a} · megadott: ${fmt(q)} ${a} · különbség: ${isZeroDecimal(diff) ? '0' : formatDecimal(diff, { sign: true, maxDp: 8 })} ${a}`;
      if (isZeroDecimal(diff)) break;
      if (isNegativeDecimal(diff)) lines.push(line(`${loc} · ${a}`, diff, PH_SUM), line(`Spending · ${a}`, negateDecimal(diff), PH_SUM));
      else lines.push(line(`${loc} · ${a}`, diff, PH_SUM), line(`Unexplained inflow · ${a}`, negateDecimal(diff), PH_SUM));
      break;
    }
    case 'result':
      lines.push(line(`${loc} · ${a}`, q, PH_SUM), line(`Trading result · ${a}`, q === null ? null : negateDecimal(q), PH_SUM));
      break;
  }
  if (isDecimalString(feeQty) && !isZeroDecimal(feeQty) && feeAsset.trim() && (isTrade(kind) || isTransfer(kind))) {
    lines.push(line(`Fees · ${feeAsset.trim()}`, feeQty, PH_SUM), credit(`${loc} · ${feeAsset.trim()}`, feeQty, PH_SUM));
  }
  return { lines, note };
}

const ledgerBalanceOf = (rows: { location_id: string; asset: string; quantity: string }[], locationId: string, asset: string): string => {
  let sum = '0';
  for (const r of rows) if (r.location_id === locationId && r.asset === asset) sum = addDecimal(sum, r.quantity);
  return sum;
};

export function EntryPage() {
  const params = useSearchParams();
  const initialKind = (KINDS.find((k) => k.value === params.get('type'))?.value ?? 'out') as EntryKind;
  const initialDeal = params.get('deal') ? Number(params.get('deal')) : null;

  const locations = useApi(getLocationItems);
  const deals = useApi(getDeals);
  const balances = useApi(getBalances);
  const suggestions = useApi(getMergeSuggestions);

  const [kind, setKind] = useState<EntryKind>(initialKind);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [asset, setAsset] = useState('USDC');
  const [quote, setQuote] = useState('USDC');
  const [qty, setQty] = useState('');
  const [ts, setTs] = useState(() => utcInputValue(new Date()));
  const [price, setPrice] = useState('');
  const [feeAsset, setFeeAsset] = useState('');
  const [feeQty, setFeeQty] = useState('');
  const [returnable, setReturnable] = useState(initialKind === 'out' || initialKind === 'move');
  const [dealId, setDealId] = useState<number | null>(initialDeal);
  const [allocQty, setAllocQty] = useState('');
  const [split, setSplit] = useState<AllocRow[] | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [note, setNote] = useState('');
  const [candle, setCandle] = useState<{ price: string | null; reason: string | null; source: string } | null>(null);
  const [interest, setInterest] = useState('');
  const [interestTouched, setInterestTouched] = useState(false);
  const [closeMode, setCloseMode] = useState(false);
  const [manualPrice, setManualPrice] = useState('');
  const [panel, setPanel] = useState<RepayPanelState | null>(null);
  const [panelKey, setPanelKey] = useState(0);
  const [helpOpen, setHelpOpen] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const locs = locations.data ?? [];
  const assetLocs = useMemo(() => locs.filter((l) => l.kind === 'asset' && !l.archived), [locs]);
  // A transfer counter is an asset location or the outside world (not the internal `Market` leg).
  const counterLocs = useMemo(() => locs.filter((l) => ((l.kind === 'asset' && !l.archived) || l.kind === 'external') && l.name !== 'Market'), [locs]);
  const findByName = (name: string, list: LocationItem[]) => list.find((l) => l.name === name)?.id ?? list[0]?.id ?? '';

  // Apply per-kind defaults once locations are known and whenever the kind changes.
  useEffect(() => {
    if (!assetLocs.length) return;
    const d = DEFAULT_LOC[kind];
    setFromId(findByName(d.from, assetLocs));
    setToId(d.to ? findByName(d.to, counterLocs) : '');
    setReturnable(kind === 'out' || kind === 'move');
    setCloseMode(false);
    setInterest('');
    setInterestTouched(false);
    setManualPrice('');
    setPanel(null);
    if (isTrade(kind)) setAsset((a) => (a === 'USDC' ? 'BTC' : a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, assetLocs.length]);

  const fromName = locs.find((l) => l.id === fromId)?.name ?? '[HELYSZÍN]';
  const toName = locs.find((l) => l.id === toId)?.name ?? '[HELYSZÍN]';

  const tsDate = parseUtcInput(ts);
  const tsIso = tsDate ? toApiUtc(tsDate) : null;

  // Price for the preview: a buy/sell without a hand-entered price (D9), or a borrow/repay without a manual price level (D49: Candle, then Binance public).
  const isLoanKind = kind === 'borrow' || kind === 'repay';
  const hasHandPrice = isTrade(kind) ? !!price.trim() : !!manualPrice.trim();
  useEffect(() => {
    setCandle(null);
    if (!(isTrade(kind) || isLoanKind) || hasHandPrice || !asset.trim() || !tsIso) return undefined;
    const ac = new AbortController();
    getCandlePrice(asset.trim().toUpperCase(), tsIso, ac.signal)
      .then((c) => { if (!ac.signal.aborted) setCandle({ price: c.price, reason: c.reason, source: c.source }); })
      .catch(() => { if (!ac.signal.aborted) setCandle({ price: null, reason: 'candle_unavailable', source: 'none' }); });
    return () => ac.abort();
  }, [kind, isLoanKind, hasHandPrice, asset, tsIso]);

  const qtyNorm = qty.trim().replace(',', '.');
  const priceNorm = price.trim().replace(',', '.');
  const effectivePrice = priceNorm || null;
  const ledgerBal = kind === 'snapshot' && balances.data ? ledgerBalanceOf(balances.data.rows, fromId, asset.trim().toUpperCase()) : null;

  const showDeal = kind !== 'snapshot';
  const isRepay = kind === 'repay';
  const assetU = asset.trim().toUpperCase();
  useEffect(() => {
    if (isRepay) { setDealId(null); setAllocQty(''); }
  }, [isRepay]);

  // D46-D48: a repay pays back chosen borrows; the interest part is typed (always available) or, in close mode, the rest of the quantity.
  const manualNorm = manualPrice.trim().replace(',', '.');
  const manualOk = !manualNorm || (isDecimalString(manualNorm) && compareDecimal(manualNorm, '0') > 0);
  const interestNorm = interest.trim().replace(',', '.');
  const closeM = isRepay && closeMode ? closeMath(qtyNorm, panel?.selectedOutstanding ?? '0', panel?.selectedCount ?? 0, interestTouched ? interestNorm : null) : null;
  const interestEff = !isRepay ? '' : closeMode && !interestTouched ? (closeM?.interest ?? '') : interestNorm;
  const interestErr = isRepay ? (closeMode && !interestTouched ? null : interestError(qtyNorm, interestEff)) : null;
  const principalEff = isRepay ? (closeMode ? (closeM?.principal ?? null) : principalPart(qtyNorm, interestEff)) : null;
  const interestPositive = isDecimalString(interestEff) && compareDecimal(interestEff, '0') > 0;
  const closesFully = isRepay && (closeMode ? closeM?.error === null : !!panel && panel.principalOutstanding !== null && compareDecimal(panel.principalOutstanding, '0') > 0 && compareDecimal(panel.linkedTotal, panel.principalOutstanding) === 0);
  const repayOk = !isRepay || (interestErr === null && (panel?.valid ?? true) && (panel?.settled ?? true) && (!closeMode || closeM?.error === null));
  const preview = buildPreview({
    kind, loc: fromName, counter: toName, asset: assetU, quote: quote.trim().toUpperCase(), qty: qtyNorm, price: effectivePrice,
    feeAsset: feeAsset.trim().toUpperCase(), feeQty: feeQty.trim().replace(',', '.'), ledgerBalance: ledgerBal,
    interest: interestEff, accrues: panel?.accrues ?? null, accrued: panel?.accrued ?? null, closesFully,
  });
  const qtyOk = isDecimalString(qtyNorm) && (kind === 'result' ? !isZeroDecimal(qtyNorm) : kind === 'snapshot' ? compareDecimal(qtyNorm, '0') >= 0 && !qtyNorm.startsWith('-') : compareDecimal(qtyNorm, '0') > 0);
  // The API requires an explicit price on a buy/sell (422 quote_and_price_required): the candle close is only a suggestion.
  const priceOk = isTrade(kind) ? isDecimalString(priceNorm) && compareDecimal(priceNorm, '0') > 0 : true;
  const feeOk = !feeQty.trim() || (isDecimalString(feeQty.trim().replace(',', '.')) && !!feeAsset.trim());
  const allocBase = kind === 'result' && qtyOk ? (isNegativeDecimal(qtyNorm) ? negateDecimal(qtyNorm) : qtyNorm) : qtyNorm;
  const allocQtyEff = (allocQty.trim() || allocBase).replace(',', '.');
  const singleAllocOk = !showDeal || dealId === null || !qtyOk || (isDecimalString(allocQtyEff) && compareDecimal(allocQtyEff, '0') > 0 && compareDecimal(allocQtyEff, allocBase) <= 0);
  const splitOk = !split || (qtyOk && validateAllocations(split, allocBase).ok);
  const locOk = !!fromId && (!isTransfer(kind) || (!!toId && toId !== fromId));
  const canSave = repayOk && manualOk && !!tsIso && qtyOk && priceOk && feeOk && !!asset.trim() && locOk && singleAllocOk && splitOk && !saving && (!isTrade(kind) || !!quote.trim());

  const submit = async () => {
    if (!tsIso) return;
    setSaving(true);
    setSaveError(null);
    setSavedAt(null);
    const allocations: NonNullable<ManualEventIn['allocations']> = !showDeal
      ? []
      : split
        ? split.filter((r) => r.dealId !== null).map((r) => ({ deal_id: r.dealId as number, quantity: r.qty }))
        : dealId !== null
          ? [{ deal_id: dealId, quantity: allocQtyEff }]
          : [];
    const body: ManualEventIn = {
      type: API_TYPE[kind],
      occurred_at: tsIso,
      location_id: fromId,
      counter_location_id: isTransfer(kind) ? toId : null,
      asset: asset.trim().toUpperCase(),
      quantity: qtyNorm,
      quote_asset: isTrade(kind) ? quote.trim().toUpperCase() : null,
      price: isTrade(kind) ? priceNorm : null,
      fee_asset: feeQty.trim() ? feeAsset.trim().toUpperCase() : null,
      fee_quantity: feeQty.trim() ? feeQty.trim().replace(',', '.') : null,
      returnable: isTransfer(kind) && kind !== 'in' ? returnable : false,
      note: note.trim(),
      allocations,
      ...(isRepay ? { interest_quantity: interestEff === '' ? '0' : interestEff } : {}),
      ...(isRepay && panel && panel.links.length > 0 ? { repay_links: panel.links } : {}),
      ...(isLoanKind && manualNorm ? { price_level: manualNorm } : {}),
    };
    try {
      await postManualEvent(body);
      setSavedAt(formatUtc(new Date()));
      setQty('');
      setAllocQty('');
      setNote('');
      setInterest('');
      setInterestTouched(false);
      setManualPrice('');
      setPanelKey((k) => k + 1);
      balances.reload();
      suggestions.reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(false);
    }
  };

  const priceHint = !isTrade(kind)
    ? 'Ennél a típusnál nincs ár.'
    : priceNorm
      ? candle?.price ? `Kézzel megadva · gyertya 1m záró: ${formatDecimal(candle.price)} USDT (D9)` : 'Kézzel megadva'
      : candle === null
        ? 'Az ár kötelező. Gyertya-ár lekérése…'
        : candle.price
          ? `Az ár kötelező. Gyertya 1m záróár erre a percre: ${formatDecimal(candle.price)} USDT (D9)`
          : 'Az ár kötelező, és erre a percre nincs gyertya-ár (nem javasolható).';

  const dealOptions = deals.data ?? [];
  const toggleHelp = (k: string) => setHelpOpen((o) => (o === k ? null : k));
  const assetPlaceholder = kind === 'snapshot' ? 'pl. USDC' : '';

  return (
    <main className="page">
      <div className="titles stack" style={{ gap: 4 }}>
        <h1>Kézi bevitel</h1>
        <div className="muted">Binance-en kívüli helyszínek (Nexo, magánszámla, egyéni) eseményei — itt a kézi bevitel az elsődleges forrás (D25). Minden időpont UTC.</div>
      </div>

      <div className="cols">
        <section className="card grow" style={{ padding: 22, gap: 18 }} aria-label="Esemény űrlap">
          <div className="field">
            <span className="flabel" id="type-l">Esemény típusa</span>
            <div className="seg big" role="group" aria-labelledby="type-l">
              {KINDS.map((k) => (
                <button key={k.value} type="button" className={kind === k.value ? 'on' : ''} aria-pressed={kind === k.value} onClick={() => { setKind(k.value); setSavedAt(null); }}>
                  {k.label}
                </button>
              ))}
            </div>
            {kind === 'snapshot' ? (
              <div className="row-wrap" style={{ alignItems: 'flex-start' }}>
                <HelpButton label="egyenleg-rögzítés" open={helpOpen === 'snapshot'} onToggle={() => toggleHelp('snapshot')} />
                {helpOpen === 'snapshot' ? <div style={{ flex: 1 }}><HelpText helpKey="snapshot" /></div> : null}
              </div>
            ) : null}
            {kind === 'result' ? (
              <div className="row-wrap" style={{ alignItems: 'flex-start' }}>
                <HelpButton label="eredmény" open={helpOpen === 'result'} onToggle={() => toggleHelp('result')} />
                {helpOpen === 'result' ? <div style={{ flex: 1 }}><HelpText helpKey="resultType" /></div> : null}
              </div>
            ) : null}
          </div>

          <Async state={locations}>
            {() => (
              <div className="grid2">
                <div className="field">
                  <label htmlFor="from">{isTransfer(kind) ? 'Honnan' : 'Helyszín'}</label>
                  <select id="from" className="input" value={fromId} onChange={(e) => setFromId(e.target.value)}>
                    {assetLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  {kind === 'borrow' || kind === 'repay' ? <span className="hint">Bármely helyszín lehet (D39); a hitel-tartozás a „{fromName} · loan” számlán jelenik meg.</span> : null}
                </div>
                {isTransfer(kind) ? (
                  <div className="field">
                    <label htmlFor="to">Hová</label>
                    <select id="to" className="input" value={toId} onChange={(e) => setToId(e.target.value)}>
                      {counterLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <span className="hint">Új helyszín felvétele: <Link to="/locations">Helyszínek</Link>.</span>
                  </div>
                ) : null}
                <div className="field">
                  <label htmlFor="asset">{kind === 'buy' ? 'Coin (ezt vetted meg)' : kind === 'sell' ? 'Coin (ezt adtad el)' : 'Coin'}</label>
                  <input id="asset" className="input" value={asset} placeholder={assetPlaceholder} onChange={(e) => setAsset(e.target.value)} />
                </div>
                {isTrade(kind) ? (
                  <div className="field">
                    <label htmlFor="quote">{kind === 'buy' ? 'Ellenérték coin (ebből vetted)' : 'Ellenérték coin (ezt kaptad érte)'}</label>
                    <input id="quote" className="input" value={quote} onChange={(e) => setQuote(e.target.value)} />
                  </div>
                ) : null}
                <div className="field">
                  <label htmlFor="qty">{kind === 'snapshot' ? 'Jelenlegi egyenleg' : kind === 'result' ? 'Eredmény (előjeles; veszteség negatív)' : kind === 'buy' ? 'Mennyiség (ennyi coint vettél)' : kind === 'sell' ? 'Mennyiség (ennyi coint adtál el)' : 'Mennyiség'}</label>
                  <input id="qty" className="input mono" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} placeholder={kind === 'result' ? 'pl. -120,5' : ''} />
                </div>
                {isRepay ? (
                  <div className="field">
                    <HelpLabel helpKey="interestPart" label="ebből kamat" className="flabel">Ebből kamat</HelpLabel>
                    <input
                      id="interest" data-testid="interest-input" aria-label="Ebből kamat" className="input mono" inputMode="decimal" aria-invalid={interestErr !== null}
                      value={closeMode && !interestTouched ? (closeM?.interest ?? '') : interest}
                      placeholder="0"
                      onChange={(e) => { setInterest(e.target.value); setInterestTouched(true); }}
                    />
                    {interestErr ? (
                      <span className="hint neg" role="alert" data-testid="interest-error">
                        {interestErr === 'invalid' ? 'A kamat nulla vagy pozitív decimális szám lehet.' : 'A kamat nem lehet több, mint a visszafizetett mennyiség.'}
                      </span>
                    ) : (
                      <span className="hint" data-testid="interest-hint">
                        {principalEff !== null ? `Tőke-rész: ${formatDecimal(principalEff)} ${assetU}. A kamat-rész sosem vár hozzárendelésre.` : 'A visszafizetett mennyiségből a kamat; üresen 0.'}
                      </span>
                    )}
                    {closeMode && interestTouched ? (
                      <button type="button" className="btn small" onClick={() => setInterestTouched(false)}>Vissza a maradékra</button>
                    ) : null}
                  </div>
                ) : null}
                <div className="field">
                  <label htmlFor="ts">Időpont (UTC)</label>
                  <input id="ts" type="text" inputMode="numeric" placeholder="ÉÉÉÉ-HH-NN ÓÓ:PP" className="input mono" value={ts} onChange={(e) => setTs(e.target.value)} />
                  <span className={`hint${tsDate ? '' : ' neg'}`}>{tsDate ? 'Minden időpont UTC (24 órás); a helyi idő nem számít.' : 'Hibás időpont: ÉÉÉÉ-HH-NN ÓÓ:PP (UTC).'}</span>
                </div>
                {isTrade(kind) ? (
                  <div className="field">
                    <HelpLabel helpKey="entryPriceField" label="ár" className="flabel">Ár</HelpLabel>
                    <input id="price" aria-label="Ár" className="input mono" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
                    <span className="hint">{priceHint}{!priceNorm && candle?.price ? <PriceSourceLabel source={candle.source} /> : null}</span>
                    {!priceNorm && candle?.price ? (
                      <button type="button" className="btn small" onClick={() => setPrice(candle.price ?? '')}>
                        A gyertya-árat használom
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {isLoanKind ? (
                  <div className="field">
                    <HelpLabel helpKey="manualPrice" label="ár kézzel" className="flabel">Ár kézzel</HelpLabel>
                    <input
                      id="manual-price" data-testid="manual-price-input" aria-label="Ár kézzel" className="input mono" inputMode="decimal" aria-invalid={!manualOk}
                      value={manualPrice} placeholder="nem kötelező" onChange={(e) => setManualPrice(e.target.value)}
                    />
                    <span className={`hint${manualOk ? '' : ' neg'}`} data-testid="manual-price-hint">
                      {!manualOk ? (
                        'Az ár pozitív decimális szám legyen.'
                      ) : manualNorm ? (
                        <>Kézi árszint: {formatDecimal(manualNorm)} <PriceSourceLabel source="manual" /> — ezt használja a szerver, a gyertya-árat nem kérdezi.</>
                      ) : candle === null ? (
                        'Üresen az árszintet a szerver számolja: Candle, majd Binance nyilvános ár; kézzel felülírhatod.'
                      ) : candle.price ? (
                        <>Várható árszint: {formatDecimal(candle.price)} <PriceSourceLabel source={candle.source} /> (a szerver számolja a végleges értéket).</>
                      ) : (
                        <>Erre a percre nincs ár ({reasonText(candle.reason)}) — add meg kézzel.</>
                      )}
                    </span>
                  </div>
                ) : null}
                {isTrade(kind) || isTransfer(kind) ? (
                  <>
                    <div className="field">
                      <label htmlFor="feeq">Díj</label>
                      <input id="feeq" className="input mono" inputMode="decimal" value={feeQty} onChange={(e) => setFeeQty(e.target.value)} placeholder="0" />
                    </div>
                    <div className="field">
                      <label htmlFor="feea">Díj coin</label>
                      <input id="feea" className="input" value={feeAsset} onChange={(e) => setFeeAsset(e.target.value)} />
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </Async>

          {isRepay ? (
            <>
              <div className="row-wrap" style={{ alignItems: 'center' }}>
                <label className="check">
                  <input
                    type="checkbox" data-testid="close-mode-toggle" checked={closeMode}
                    onChange={(e) => { setCloseMode(e.target.checked); setInterestTouched(false); }}
                  />
                  <span>Teljes lezárás</span>
                </label>
                <HelpButton label="teljes lezárás" open={helpOpen === 'close'} onToggle={() => toggleHelp('close')} />
                {closeMode && panel && compareDecimal(panel.selectedOutstanding, '0') > 0 ? (
                  <button type="button" className="btn small" data-testid="close-fill-principal" onClick={() => { setQty(panel.selectedOutstanding); setInterestTouched(false); }}>
                    Csak a tőke: {formatDecimal(panel.selectedOutstanding)} {assetU}
                  </button>
                ) : null}
              </div>
              {helpOpen === 'close' ? <HelpText helpKey="closeMode" /> : null}
              <RepayPanel
                key={panelKey} mode="create" locationId={fromId} locationName={fromName} asset={assetU} quantity={qtyNorm} interest={interestEff}
                occurredAt={tsIso} closeMode={closeMode} onChange={setPanel} onFill={(q) => setQty(q)}
              />
              {closeMode && closeM?.error === 'qty_below_principal' ? (
                <div className="notice err small" role="alert" data-testid="close-error">
                  A megadott mennyiség kisebb, mint a kijelölt hitelek kint lévő része ({formatDecimal(panel?.selectedOutstanding ?? '0')} {assetU}): teljes lezáráshoz legalább ennyit kell fizetni.
                </div>
              ) : null}
              {closeMode && closeM?.error === 'principal_mismatch' ? (
                <div className="notice err small" role="alert" data-testid="close-error">
                  Teljes lezárásnál a tőke (mennyiség − kamat) pontosan egyezzen a kijelölt hitelek kint lévő részével ({formatDecimal(panel?.selectedOutstanding ?? '0')} {assetU}); most {formatDecimal(closeM.principal ?? '0')} {assetU}.
                </div>
              ) : null}
              {closeMode && closeM?.error === 'no_loan_selected' ? (
                <div className="notice err small" role="alert" data-testid="close-error">Jelölj ki legalább egy lezárandó hitelt.</div>
              ) : null}
              {panel && !panel.settled ? <div className="hint" data-testid="repay-pending">A javaslat betöltése…</div> : null}
              {closeMode || (closesFully && interestPositive) ? (
                <div className="box" data-testid="close-preview">
                  <b>{closeMode ? 'Teljes lezárás' : 'Teljes visszafizetés'} – könyvelési előnézet</b>
                  <div className="mono small" data-testid="close-principal">Tőke: {principalEff !== null ? `${formatDecimal(principalEff)} ${assetU}` : '—'}</div>
                  <div className="mono small" data-testid="close-interest">
                    Kamat: {isDecimalString(interestEff) ? `${formatDecimal(interestEff)} ${assetU}` : '—'}
                    {panel?.accrues ? ' · a már számolt kamatot rendezi' : ' · kamatköltségként (Interest) könyvelődik'}
                  </div>
                  {panel?.accrues && panel.accrued !== null && isDecimalString(interestEff) && interestPositive ? (
                    <div className="small" data-testid="close-trueup">
                      Várható kamat-kiigazítás (true-up): <b className="mono">{formatDecimal(expectedTrueUp(interestEff, panel.accrued), { sign: true })} {assetU}</b>
                      {' '}(kamat − számolt kamat: {formatDecimal(panel.accrued)} {assetU}). Várható érték: a végleges kiigazítást a szerver számolja, a hitel-számla pontosan nullára zár.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          {showDeal ? (
            <div className="box">
              <div className="card-head">
                <b>{isRepay ? 'Eredet (nem kötelező)' : 'Eredet és ügylet'}</b>
                <button type="button" className="btn small" disabled={!qtyOk} onClick={() => { if (!split) setSplit([newAllocRow(dealId, allocBase)]); setSplitOpen(true); }}>
                  Felosztás több ügylet közt…
                </button>
              </div>
              {split ? (
                <div className="notice ok small" role="status">
                  Felosztva {split.length} sorra.{' '}
                  <button type="button" className="btn small" onClick={() => setSplit(null)}>Felosztás törlése</button>
                </div>
              ) : isRepay ? (
                <div className="hint">Eredet-címke: <b style={{ color: 'var(--text)' }}>ügylet nélkül</b> — a visszafizetés a lent kijelölt hiteleket csökkenti; ügyletbe osztáshoz használd a felosztást.</div>
              ) : (
                <div className="grid2">
                  <div className="field">
                    <label htmlFor="deal">Ügylet (opcionális)</label>
                    <select id="deal" aria-describedby="deal-hint" className="input" value={dealId ?? ''} onChange={(e) => setDealId(e.target.value ? Number(e.target.value) : null)}>
                      {dealOptions.filter((d) => d.status === 'open' || d.id === dealId).map((d) => <option key={d.id} value={d.id}>#{d.id} {d.name}</option>)}
                      <option value="">{kind === 'borrow' ? 'Ügylet nélkül' : 'Nincs (saját tőke)'}</option>
                    </select>
                    <span className="hint" id="deal-hint">Nem kötelező: az eseményt egy ügylethez sorolja. Visszafizetésnél a hiteleket a Visszafizetés panelben jelölöd ki, ügylet nélkül is.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="alloc">Allokált mennyiség</label>
                    <input id="alloc" className="input mono" inputMode="decimal" value={allocQty} placeholder={allocBase} onChange={(e) => setAllocQty(e.target.value)} disabled={dealId === null} />
                  </div>
                </div>
              )}
              {!singleAllocOk ? <div className="notice err small" role="alert">Az allokált mennyiség pozitív és legfeljebb az esemény mennyisége lehet.</div> : null}
              {(kind === 'out' || kind === 'move') ? (
                <div className="stack" style={{ gap: 6 }}>
                  <label className="check">
                    <input type="checkbox" checked={returnable} onChange={(e) => setReturnable(e.target.checked)} />
                    Visszahozandó a Binance Marginra
                  </label>
                  <div className="row-wrap" style={{ alignItems: 'flex-start' }}>
                    <HelpButton label="visszahozandó jelölés" open={helpOpen === 'ret'} onToggle={() => toggleHelp('ret')} />
                    {helpOpen === 'ret' ? <div style={{ flex: 1 }}><HelpText helpKey="returnableFlag" /></div> : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="note">Megjegyzés</label>
            <textarea id="note" className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          {savedAt ? <Notice kind="ok" title="Esemény rögzítve">A tétel a naplóban van ({savedAt} UTC). A könyvelési sorokat a szerver állította elő.</Notice> : null}
          {saveError ? <ErrorNotice error={saveError} /> : null}
          <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
            <Link className="btn" to="/overview">Mégse</Link>
            <button type="button" className="btn primary" disabled={!canSave} onClick={() => void submit()}>
              {saving ? 'Rögzítés…' : 'Rögzítés'}
            </button>
          </div>
        </section>

        <aside style={{ flex: '1 1 400px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <section className="card" aria-label="Könyvelési előnézet">
            <HelpLabel helpKey="preview" label="könyvelési előnézet" className="" as="h2">Könyvelési előnézet</HelpLabel>
            <span className="hint">Kettős könyvelés: minden tétel két helyszín egyenlegét mozgatja (D21). Előnézet; a szerver számol.</span>
            <table className="ledger">
              <thead>
                <tr><th>Számla</th><th className="num">Tartozik</th><th className="num">Követel</th></tr>
              </thead>
              <tbody>
                {preview.lines.length === 0 ? (
                  <tr><td colSpan={3} className="muted">Nincs könyvelendő különbség.</td></tr>
                ) : (
                  preview.lines.map((l, i) => (
                    <tr key={`${l.acct}-${i}`}>
                      <td>{l.acct}</td>
                      <td className="num mono">{l.dr}</td>
                      <td className="num mono">{l.cr}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {preview.note ? <div className="hint" data-testid="preview-note">{preview.note}</div> : null}
            {showDeal ? (
              <div className="hint">Eredet-címke: <b style={{ color: 'var(--text)' }}>{split ? 'több ügylet' : dealId !== null ? `#${dealId} ${dealOptions.find((d) => d.id === dealId)?.name ?? ''}` : isRepay || kind === 'borrow' ? 'ügylet nélkül' : 'saját tőke'}</b> — öröklődik a tétel minden további konverziójára és transzferére.</div>
            ) : null}
          </section>

          <Async state={suggestions}>
            {(list) => (list.length ? <>{list.map((s) => <MergeCard key={s.id} s={s} onDone={() => suggestions.reload()} />)}</> : null)}
          </Async>
        </aside>
      </div>

      {splitOpen && split ? (
        <Dialog title="Felosztás több ügylet közt" onClose={() => setSplitOpen(false)}>
          <AllocationEditor rows={split} onChange={setSplit} deals={dealOptions.map((d) => ({ id: d.id, name: d.name }))} total={allocBase} asset={asset.trim().toUpperCase() || 'coin'} />
          <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={() => { setSplit(null); setSplitOpen(false); }}>Mégse</button>
            <button type="button" className="btn primary" disabled={!validateAllocations(split, allocBase).ok} onClick={() => setSplitOpen(false)}>Kész</button>
          </div>
        </Dialog>
      ) : null}
    </main>
  );
}

function MiniEvent({ title, e, detail }: { title: string; e: MergeSuggestion['manual']; detail?: string | null }) {
  return (
    <div className="box" style={{ padding: 10, gap: 2, fontSize: 13 }}>
      <span className="label">{title}</span>
      <span className="mono">{formatDecimal(e.quantity)} {e.asset}</span>
      <span className="mono muted">{formatUtc(e.occurred_at)} UTC</span>
      <span className="muted">{locationText(e)}</span>
      {detail ? <span className="muted">{detail}</span> : <span className="muted">{eventTypeText(e)}</span>}
    </div>
  );
}

function MergeCard({ s, onDone }: { s: MergeSuggestion; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const decide = async (d: 'accept' | 'reject') => {
    setBusy(true);
    setErr(null);
    try {
      await postMergeDecision(s.id, d);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card warnb" aria-label="Összefésülési javaslat">
      <span className="label warn">Összefésülési javaslat</span>
      <div>A Binance-listában van egy hasonló esemény. Ugyanaz?</div>
      <div className="grid2" style={{ gap: 10 }}>
        <MiniEvent title="Kézi" e={s.manual} />
        <MiniEvent title="Binance" e={s.binance} detail={s.binance_detail} />
      </div>
      <div className="hint">Jóváhagyás után a tőzsdei rekord a forrás, a kézi bevitel hozzá kapcsolva marad (D29).</div>
      {err ? <ErrorNotice error={err} /> : null}
      <div className="row-wrap">
        <button type="button" className="btn primary" disabled={busy} onClick={() => void decide('accept')}>Igen, összefésülöm</button>
        <button type="button" className="btn" disabled={busy} onClick={() => void decide('reject')}>Nem, külön esemény</button>
      </div>
    </section>
  );
}
